import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin, Play, Search, X } from "lucide-react";
import { PATREON_URL, CLUB_PERKS, JOIN_URL } from "@/lib/club";
import { CHAT, WORDS, FAVES, BRANDS } from "./concept-content";
import { nextClasses } from "./ClubRichThread";
import { useVideoCards, useMenu, FOLLOW, footerLinks, Signup } from "./ConceptBits";
import "./HomeRound5.css";
import "./HomeBlue.css";

/* Concept from the blue "Hi, I'm chelsea" mockup. The mockup's model is not
   Chelsea, so the hero uses her real cutout; the laptop and mug are cut from
   the mockup. Positions are mockup pixels on a 1536 × 1024 stage. */

const STAGE = { w: 1536, h: 1024 };
export const EASE = [0.22, 1, 0.36, 1] as const;
const CLUB = "/club-preview/blue";

const box = (x: number, y: number, w?: number, h?: number): CSSProperties => ({
  left: `${(x / STAGE.w) * 100}%`, top: `${(y / STAGE.h) * 100}%`,
  ...(w !== undefined && { width: `${(w / STAGE.w) * 100}%` }),
  ...(h !== undefined && { height: `${(h / STAGE.h) * 100}%` }),
});

export function useFonts() {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght,SOFT@9..144,600..900,0..100&family=Gochi+Hand&family=Amatic+SC:wght@700&family=Figtree:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
    return () => link.remove();
  }, []);
}

export function Draw({ d, delay = 0, width = 3 }: { d: string; delay?: number; width?: number }) {
  return <motion.path d={d} fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"
    initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
    transition={{ pathLength: { duration: 0.55, delay, ease: "easeOut" }, opacity: { duration: 0.01, delay } }} />;
}

function Hand({ children, delay, className, style }: { children: ReactNode; delay: number; className: string; style?: CSSProperties }) {
  return <motion.div className={className} style={style} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay, duration: 0.45, ease: EASE }}>{children}</motion.div>;
}

const MENU = [
  { to: "/about", label: "About", note: "hi, it’s me!" },
  { to: "/videos", label: "Projects", note: "new every week" },
  { to: CLUB, label: "The club", note: "sew with me live" },
  { to: "/shop", label: "Shop" },
  { to: "/guides", label: "Journal" },
];

/** Phone menu: the blue page opens from the toggle like a pink-on-blue poster. */
function Menu({ menu }: { menu: ReturnType<typeof useMenu> }) {
  const at = "circle(0% at calc(100% - 38px) 38px)";
  return <AnimatePresence>
    {menu.open && <motion.div id="hb-menu" ref={menu.panel} className="hb-menu" role="dialog" aria-modal="true" aria-label="Menu"
      initial={{ clipPath: at }} animate={{ clipPath: "circle(150% at calc(100% - 38px) 38px)" }} exit={{ clipPath: at }} transition={{ duration: 0.5, ease: EASE }}>
      <div className="hb-menu-top">
        <Link to="/home-preview/blue" className="hb-logo">A Few Loose Threads</Link>
        <button type="button" className="hb-burger" onClick={() => menu.setOpen(false)} aria-label="Close menu"><X size={24} aria-hidden="true" /></button>
      </div>
      <p className="hb-menu-hand">where to?</p>
      <nav aria-label="Main">
        <ul>{MENU.map((m, i) => <motion.li key={m.to} initial={{ opacity: 0, y: 30, rotate: -6 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ delay: 0.15 + i * 0.06, duration: 0.5, ease: EASE }}>
          <Link to={m.to}>{m.label}</Link>{m.note && <span className="hb-note">{m.note}</span>}
        </motion.li>)}</ul>
      </nav>
      <motion.div className="hb-menu-foot" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
        <Link to={CLUB} className="hb-btn hb-btn-yellow">Sew with me <ArrowRight size={18} aria-hidden="true" /></Link>
        <ul className="hb-foot-social">{FOLLOW.map(f => <li key={f.s.key}><a href={f.s.url} target="_blank" rel="noreferrer" aria-label={`${f.s.platform} ${f.s.handle}`}>{f.icon}</a></li>)}</ul>
      </motion.div>
      <svg className="hb-menu-heart" viewBox="0 0 30 46" aria-hidden="true"><Draw d="M15 42 C 4 32, 0 22, 3 14 C 6 6, 13 6, 15 14 C 17 6, 25 6, 27 14 C 30 22, 26 32, 15 42 Z" delay={0.6} width={3.4} /></svg>
    </motion.div>}
  </AnimatePresence>;
}

/** On the club page the club link is current and the button goes straight to checkout. */
export function Nav({ club = false }: { club?: boolean }) {
  const menu = useMenu();
  return <header className="hb-nav">
    <Link to="/home-preview/blue" className="hb-logo">A Few Loose Threads</Link>
    <nav aria-label="Main">
      <Link to="/about">About</Link>
      <Link to="/videos">Projects</Link>
      <Link to={CLUB} aria-current={club ? "page" : undefined}>The Club</Link>
      <Link to="/shop">Shop</Link>
      <Link to="/guides">Journal</Link>
    </nav>
    <Link to="/guides" className="hb-search" aria-label="Search"><Search size={22} /></Link>
    {club
      ? <a href={JOIN_URL} className="hb-cta">Join the club <ArrowRight size={18} aria-hidden="true" /></a>
      : <Link to={CLUB} className="hb-cta">Sew with me <ArrowRight size={18} aria-hidden="true" /></Link>}
    <button ref={menu.toggle} type="button" className="hb-burger" aria-label="Open menu" aria-expanded={menu.open} aria-controls="hb-menu" onClick={() => menu.setOpen(true)}>
      <svg viewBox="0 0 28 20" aria-hidden="true"><path d="M2 3 C 10 1, 18 4, 26 2" /><path d="M2 10 C 9 11, 19 9, 26 10" /><path d="M2 17 C 10 18, 17 16, 26 18" /></svg>
    </button>
    <Menu menu={menu} />
  </header>;
}

const POLAROIDS = [
  { src: "/chelsea-and-cosmo.jpg", pos: "62% 72%", zoom: 2.1, caption: <>Chief quality<br />inspector <small>(a.k.a. Cosmo)</small></>, alt: "Cosmo the cat", at: [1030, 680, 150, 236], r: -10, tape: null },
  { src: "/img/products/qs-mat.jpg", pos: "50% 30%", zoom: 1.5, caption: <>Fabric makes<br />me happy.</>, alt: "A tall stack of colorful folded fabric", at: [1180, 640, 168, 238], r: 4, tape: "pink" },
  { src: "/chelsea-rainbow-portrait.jpg", pos: "58% 26%", zoom: 1.6, caption: <>Same me.<br />More sewing.<br />Let’s go.</>, alt: "Chelsea smiling in her straw hat", at: [1312, 660, 196, 290], r: 6, tape: "green" },
] as const;

function Hero() {
  return <section className="hb-hero" aria-labelledby="hb-title">
    <div className="hb-stage">
      <Nav />

      <h1 id="hb-title" className="hb-title">
        <span className="hb-hi" style={box(136, 108)}>Hi, I’m</span>
        <span className="hb-name" style={box(48, 112)} aria-label="Chelsea">
          {"chelsea".split("").map((c, i) => <motion.span key={i} aria-hidden="true" initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: 0.1 + i * 0.05, duration: 0.6, ease: EASE }}>{c}</motion.span>)}
        </span>
      </h1>
      <svg className="hb-abs hb-yellow" style={box(136, 166, 170, 30)} viewBox="0 0 170 30" aria-hidden="true"><Draw d="M4 22 C 50 12, 110 10, 166 8" delay={0.7} width={5} /></svg>

      <motion.img className="hb-abs hb-chelsea" src="/chelsea-cutout-trim.webp" alt="Chelsea smiling with one arm raised, in a straw hat and a colorful checked sundress"
        style={box(548, 76, 420, 1150)} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.7, ease: EASE }} />

      <Hand className="hb-abs hb-script hb-make" style={box(104, 512)} delay={0.9}>I make colorful<br />things (and I’d love<br />for you to make<br />them too).</Hand>
      <svg className="hb-abs hb-yellow" style={box(398, 536, 42, 62)} viewBox="0 0 42 62" aria-hidden="true">
        <Draw d="M6 18 L32 4" delay={1.3} width={4.5} /><Draw d="M10 34 L38 30" delay={1.38} width={4.5} /><Draw d="M8 50 L30 56" delay={1.46} width={4.5} />
      </svg>

      <Hand className="hb-abs hb-copy" style={box(132, 690, 330)} delay={1.0}>
        <p>Sewing, creativity, and a community of good people. Welcome to my corner of the internet.</p>
        <div className="hb-actions">
          <Link to="/about" className="hb-btn hb-btn-yellow">Get to know me <ArrowRight size={16} aria-hidden="true" /></Link>
          <a href="#making" className="hb-btn hb-btn-ghost"><Play size={14} fill="currentColor" aria-hidden="true" /> Watch my latest</a>
        </div>
      </Hand>

      <Hand className="hb-abs hb-marker hb-same" style={box(1132, 150)} delay={1.2}>Same girl.<br /><span>New project.</span></Hand>
      <svg className="hb-abs hb-yellow" style={box(1088, 172, 30, 46)} viewBox="0 0 30 46" aria-hidden="true"><Draw d="M15 42 C 4 32, 0 22, 3 14 C 6 6, 13 6, 15 14 C 17 6, 25 6, 27 14 C 30 22, 26 32, 15 42 Z" delay={1.45} width={3.6} /></svg>

      <ul className="hb-abs hb-checks" style={box(1052, 482)} aria-label="What you’ll find here">
        {["Sewing", "Good vibes", "Cool people", "Big ideas"].map((t, i) => <li key={t}>
          <svg viewBox="0 0 26 26" aria-hidden="true"><rect x="2" y="2" width="22" height="22" rx="4" fill="none" stroke="currentColor" strokeWidth="2.4" /><Draw d="M7 13 L11.5 17.5 L19.5 8" delay={1.5 + i * 0.22} width={2.8} /></svg>
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.45 + i * 0.22 }}>{t}</motion.span>
        </li>)}
      </ul>

      <Link to={CLUB} className="hb-abs hb-youin" style={box(1338, 508, 136, 104)} aria-label="You in? Join the club">
        <svg viewBox="0 0 136 104" aria-hidden="true"><Draw d="M96 12 C 60 0, 14 14, 8 48 C 2 82, 52 100, 96 90 C 132 80, 136 40, 112 20 C 100 10, 80 8, 64 12" delay={2.3} width={3.4} /></svg>
        <motion.span initial={{ opacity: 0, rotate: -18 }} animate={{ opacity: 1, rotate: -14 }} transition={{ delay: 2.1 }}>You<br />in? ♡</motion.span>
      </Link>

      {POLAROIDS.map((p, i) => <motion.figure key={p.src} className="hb-abs hb-polaroid" style={{ ...box(p.at[0], p.at[1], p.at[2], p.at[3]) }}
        initial={{ opacity: 0, y: 40, rotate: p.r + 12 }} animate={{ opacity: 1, y: 0, rotate: p.r }} whileHover={{ y: -6, rotate: p.r / 2, zIndex: 9 }}
        transition={{ delay: 0.9 + i * 0.15, type: "spring", stiffness: 140, damping: 15 }}>
        {p.tape && <i className={`hb-tape hb-tape-${p.tape}`} aria-hidden="true" />}
        <span className="hb-polaroid-img"><img src={p.src} alt={p.alt} style={{ objectPosition: p.pos, transform: `scale(${p.zoom})`, transformOrigin: p.pos }} /></span>
        <figcaption>{p.caption} <b aria-hidden="true">♥</b></figcaption>
      </motion.figure>)}

      <div className="hb-desk" aria-hidden="true" />
      <img className="hb-abs hb-laptop" src="/concept/laptop.png" alt="" style={box(0, 814, 292, 210)} />
      <img className="hb-abs hb-mug" src="/concept/mug.png" alt="" style={box(380, 860, 96, 113)} />
    </div>
  </section>;
}

/* ─── Sections ─── */

export const VIEW = { once: true, margin: "-80px" } as const;
const DENVER = "America/Denver";
export const datePart = (d: Date, o: Intl.DateTimeFormatOptions) => d.toLocaleDateString("en-US", { timeZone: DENVER, ...o });

/** A doodle stroke that draws itself when scrolled into view. */
export function Ink({ d, delay = 0, width = 3 }: { d: string; delay?: number; width?: number }) {
  return <motion.path d={d} fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"
    initial={{ pathLength: 0, opacity: 0 }} whileInView={{ pathLength: 1, opacity: 1 }} viewport={VIEW}
    transition={{ pathLength: { duration: 0.6, delay, ease: "easeOut" }, opacity: { duration: 0.01, delay } }} />;
}

export const HEART = "M15 42 C 4 32, 0 22, 3 14 C 6 6, 13 6, 15 14 C 17 6, 25 6, 27 14 C 30 22, 26 32, 15 42 Z";

/** The hero's "Hi, I'm / chelsea" move, reused as every section title. */
export function Head({ id, hand, word, tone, className = "" }: { id: string; hand: string; word: string; tone: "pink" | "blue"; className?: string }) {
  return <h2 id={id} className={`hb-head hb-head-${tone} ${className}`}>
    <motion.span className="hb-head-hand" initial={{ opacity: 0, rotate: -12 }} whileInView={{ opacity: 1, rotate: -5 }} viewport={VIEW} transition={{ delay: 0.25, duration: 0.5, ease: EASE }}>{hand}</motion.span>
    <motion.span className="hb-head-word" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ duration: 0.7, ease: EASE }}>{word}</motion.span>
  </h2>;
}

const TAPES = ["pink", "green", "yellow"] as const;
const TILT = [-3, 2, -1.5];

function Making() {
  const cards = useVideoCards(3);
  return <section id="making" className="hb-sec hb-making" aria-labelledby="hb-making-title">
    <div className="hb-wrap">
      <div className="hb-row">
        <Head id="hb-making-title" hand="what I’m" word="making" tone="blue" />
        <div className="hb-row-aside">
          <p className="hb-note">new videos every week!</p>
          <svg className="hb-arrow" viewBox="0 0 80 60" aria-hidden="true"><Ink d="M6 8 C 30 6, 56 18, 64 48" delay={0.6} /><Ink d="M50 40 L65 50 L72 34" delay={1} /></svg>
          <Link to="/videos" className="hb-btn hb-btn-blue">All videos <ArrowRight size={16} aria-hidden="true" /></Link>
        </div>
      </div>

      <div className="hb-making-grid">
        {cards.map((c, i) => <motion.a key={c.id} className="hb-snap" href={c.url} target="_blank" rel="noreferrer"
          initial={{ opacity: 0, y: 40, rotate: TILT[i] + 6 }} whileInView={{ opacity: 1, y: 0, rotate: TILT[i] }} whileHover={{ y: -8, rotate: 0 }} viewport={VIEW}
          transition={{ delay: i * 0.1, type: "spring", stiffness: 140, damping: 16 }}>
          <i className={`hb-snap-tape hb-tape-${TAPES[i]}`} aria-hidden="true" />
          <span className="hb-snap-img"><img src={c.img} alt="" style={{ objectPosition: c.pos }} /><b><Play size={18} fill="currentColor" aria-hidden="true" /></b></span>
          <span className="hb-snap-tag">{c.tag}</span>
          <span className="hb-snap-title">{c.title}</span>
        </motion.a>)}

        <div className="hb-follow">
          <span className="hb-follow-photo"><img src="/chelsea-and-cosmo.jpg" alt="Cosmo the cat on Chelsea’s lap at the sewing machine" /></span>
          <p className="hb-note">Cosmo approves every video.</p>
          <p className="hb-follow-label">Follow along</p>
          <ul>{FOLLOW.map(f => <li key={f.s.key}><a href={f.s.url} target="_blank" rel="noreferrer" aria-label={`${f.s.platform} ${f.s.handle}`}>{f.icon}</a></li>)}</ul>
        </div>
      </div>
    </div>
  </section>;
}

/** The live-class preview: a taped-up screen with the chat rolling in. */
export function OnAir() {
  const [n, setN] = useState(1);
  useEffect(() => { const t = setInterval(() => setN(v => v + 1), 2300); return () => clearInterval(t); }, []);
  const shown = Array.from({ length: Math.min(n, 3) }, (_, i) => { const k = n - Math.min(n, 3) + i; return { ...CHAT[k % CHAT.length], k }; });
  return <motion.figure className="hb-onair" initial={{ opacity: 0, y: 40, rotate: 5 }} whileInView={{ opacity: 1, y: 0, rotate: 2 }} viewport={VIEW} transition={{ type: "spring", stiffness: 120, damping: 16 }}>
    <i className="hb-snap-tape hb-tape-yellow" aria-hidden="true" />
    <span className="hb-onair-screen">
      <img src="/chelsea-rainbow-portrait.jpg" alt="Chelsea smiling and waving during a live class" />
      <span className="hb-onair-live"><i />Live</span>
      <ul aria-label="Sample class chat">
        <AnimatePresence initial={false}>
          {shown.map(c => <motion.li key={c.k} layout initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, transition: { duration: 0.2 } }} transition={{ duration: 0.4, ease: EASE }}><b>{c.who}</b>{c.text}</motion.li>)}
        </AnimatePresence>
      </ul>
    </span>
    <figcaption>Tuesday night. Pajamas encouraged.</figcaption>
  </motion.figure>;
}

function Club() {
  const dates = nextClasses(2);
  return <section id="club" className="hb-sec hb-club" aria-labelledby="hb-club-title">
    <div className="hb-wrap hb-club-grid">
      <div className="hb-club-copy">
        <p className="hb-kicker">Loose Threads Club · my Patreon</p>
        <Head id="hb-club-title" hand="come sew" word="with me" tone="pink" />
        <p className="hb-lede">Twice a month we pick one big project and I teach it live. Ask questions, sew along, and hang out with the nicest people on the internet.</p>
        <motion.ul className="hb-club-checks" initial="off" whileInView="on" viewport={VIEW}>
          {CLUB_PERKS.map((p, i) => <li key={p.title}>
            <svg viewBox="0 0 26 26" aria-hidden="true"><rect x="2" y="2" width="22" height="22" rx="4" fill="none" stroke="currentColor" strokeWidth="2.4" />
              <motion.path d="M7 13 L11.5 17.5 L19.5 8" fill="none" stroke="currentColor" strokeWidth={2.8} strokeLinecap="round" strokeLinejoin="round"
                variants={{ off: { pathLength: 0, opacity: 0 }, on: { pathLength: 1, opacity: 1, transition: { delay: 0.4 + i * 0.25, duration: 0.45, opacity: { delay: 0.4 + i * 0.25, duration: 0.01 } } } }} /></svg>
            <span><b>{p.title}</b>{p.description}</span>
          </li>)}
        </motion.ul>
        <div className="hb-club-join">
          <span className="hb-youin-wrap">
            <a href={PATREON_URL} target="_blank" rel="noreferrer" className="hb-btn hb-btn-yellow hb-btn-big">Join the club <ArrowRight size={18} aria-hidden="true" /></a>
            <svg className="hb-youin-ring" viewBox="0 0 300 110" preserveAspectRatio="none" aria-hidden="true"><Ink d="M210 10 C 120 -4, 20 12, 8 52 C -2 92, 120 106, 220 98 C 300 90, 304 40, 260 18 C 240 8, 214 6, 190 9" delay={1.4} width={3} /></svg>
          </span>
          <Link to={CLUB} className="hb-btn hb-btn-ghost">See what’s inside</Link>
        </div>
        <p className="hb-club-foot">Just want to cheer me on? <a href={PATREON_URL} target="_blank" rel="noreferrer">Thread Friend is $3/month</a> ♡</p>
      </div>

      <div className="hb-club-side">
        <motion.p className="hb-price" initial={{ scale: 0, rotate: -40 }} whileInView={{ scale: 1, rotate: -12 }} viewport={VIEW} transition={{ delay: 0.5, type: "spring", stiffness: 220, damping: 13 }}>
          <b>$25</b>a month<small>cancel anytime</small>
        </motion.p>
        <OnAir />
        <div className="hb-dates">
          <p className="hb-note">next classes!</p>
          <svg className="hb-arrow hb-arrow-down" viewBox="0 0 80 60" aria-hidden="true"><Ink d="M10 6 C 20 30, 40 44, 66 48" delay={0.9} /><Ink d="M52 36 L67 48 L52 58" delay={1.3} /></svg>
          <ul>{dates.map((d, i) => <motion.li key={d.toISOString()} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0, rotate: i ? 4 : -3 }} viewport={VIEW} transition={{ delay: 0.7 + i * 0.15, duration: 0.5, ease: EASE }}>
            <small>{datePart(d, { weekday: "short" })}</small><b>{datePart(d, { day: "numeric" })}</b><span>{datePart(d, { month: "short" })} · 7 PM MT</span>
          </motion.li>)}</ul>
        </div>
      </div>
    </div>
  </section>;
}

const STICKY = ["yellow", "white", "blue"] as const;

function KindWords() {
  return <section className="hb-sec hb-words" aria-labelledby="hb-words-title">
    <div className="hb-wrap">
      <Head id="hb-words-title" hand="a few" word="kind words" tone="blue" className="hb-head-center" />
      <div className="hb-words-grid">
        {WORDS.map((w, i) => <motion.figure key={w.name} className={`hb-sticky hb-sticky-${STICKY[i]}`}
          initial={{ opacity: 0, y: 40, rotate: [-4, 3, -2][i] + 6 }} whileInView={{ opacity: 1, y: 0, rotate: [-4, 3, -2][i] }} whileHover={{ rotate: 0, y: -6 }} viewport={VIEW}
          transition={{ delay: i * 0.12, type: "spring", stiffness: 130, damping: 15 }}>
          <i className={`hb-snap-tape hb-tape-${["pink", "yellow", "green"][i]}`} aria-hidden="true" />
          <blockquote>“{w.quote}”</blockquote>
          <figcaption><b>{w.name}</b>{w.tag}</figcaption>
        </motion.figure>)}
      </div>
    </div>
  </section>;
}

/* Cards hang off a sagging string; tops follow the curve M0 10 Q500 70 1000 10. */
const SAG = [27, 40, 27];

function Favorites() {
  return <section id="favorites" className="hb-sec hb-faves" aria-labelledby="hb-faves-title">
    <div className="hb-wrap">
      <div className="hb-row">
        <Head id="hb-faves-title" hand="stuff I" word="love" tone="pink" />
        <div className="hb-row-aside">
          <p className="hb-note">tools, machines, and fabric I actually use</p>
          <Link to="/favorites" className="hb-btn hb-btn-blue">See all my favorites <ArrowRight size={16} aria-hidden="true" /></Link>
        </div>
      </div>
      <div className="hb-line">
        <svg className="hb-line-string" viewBox="0 0 1000 60" preserveAspectRatio="none" aria-hidden="true"><path d="M-20 6 Q500 74 1020 6" /></svg>
        <ul>
          {FAVES.map((f, i) => { const r = [-4, 2, 5][i]; return <motion.li key={f.name} style={{ marginTop: SAG[i] }}
            initial={{ opacity: 0, y: -30, rotate: r * 2 }} whileInView={{ opacity: 1, y: 0, rotate: r }} whileHover={{ rotate: [r, r - 5, r + 3, r] }} viewport={VIEW}
            transition={{ delay: i * 0.12, type: "spring", stiffness: 120, damping: 10 }}>
            <i className="hb-pin" aria-hidden="true" />
            <Link to="/favorites" className="hb-fave">
              <span className="hb-fave-img"><img src={f.img} alt="" /></span>
              <span className="hb-fave-tag">{f.tag}</span>
              <b>{f.name}</b>
              <span className="hb-note">{f.why}</span>
            </Link>
          </motion.li>; })}
        </ul>
      </div>
      <p className="hb-disclose">Some links are affiliate links. If you buy through them, I may earn a small commission at no extra cost to you.</p>
    </div>
  </section>;
}

function WhereIllBe() {
  const [next] = nextClasses(1);
  return <section className="hb-sec hb-where" aria-labelledby="hb-where-title">
    <div className="hb-wrap">
      <div className="hb-row">
        <Head id="hb-where-title" hand="come say" word="hi!" tone="pink" />
        <div className="hb-row-aside">
          <p className="hb-note">I’d love to meet you in real life (or on a Tuesday night).</p>
        </div>
      </div>
      <div className="hb-where-grid">
        <motion.div className="hb-ticket-wrap" initial={{ opacity: 0, y: 30, rotate: 0 }} whileInView={{ opacity: 1, y: 0, rotate: -2 }} viewport={VIEW} transition={{ duration: 0.6, ease: EASE }}>
          <Link to="/events/stitches-night-out" className="hb-ticket">
            <span className="hb-ticket-img"><img src="/events/practical-magic-2.jpg" alt="" /></span>
            <span className="hb-ticket-body">
              <small>Admit one</small>
              <b>Stitches Night Out</b>
              <span className="hb-ticket-what">A sewists’ movie night</span>
              <span><MapPin size={15} aria-hidden="true" /> American Fork, UT</span>
            </span>
            <span className="hb-ticket-stub"><small>Oct</small><b>20</b><span>Tickets <ArrowUpRight size={14} aria-hidden="true" /></span></span>
          </Link>
        </motion.div>
        <Link to="/retreats" className="hb-spot hb-spot-pink">
          <small>In person</small>
          <b>Sewing retreats</b>
          <span>A few days away to sew, eat, and laugh with me.</span>
          <em>See retreats <ArrowRight size={15} aria-hidden="true" /></em>
        </Link>
        <Link to={CLUB} className="hb-spot hb-spot-yellow">
          <small>Online · the club</small>
          <b>Next live class</b>
          <span className="hb-spot-date">{datePart(next, { weekday: "long", month: "short", day: "numeric" })}
            <svg viewBox="0 0 240 70" preserveAspectRatio="none" aria-hidden="true"><Ink d="M180 8 C 110 -2, 14 8, 8 36 C 2 64, 120 70, 200 60 C 250 52, 244 18, 200 10" delay={0.6} width={2.6} /></svg>
          </span>
          <em>Join me, 7 PM MT <ArrowRight size={15} aria-hidden="true" /></em>
        </Link>
      </div>
    </div>
  </section>;
}

function Brands() {
  return <section className="hb-brands" aria-labelledby="hb-brands-title">
    <h2 id="hb-brands-title" className="hb-note">brands &amp; friends I’ve made things with</h2>
    <ul>{BRANDS.map(b => <li key={b.name}><img src={b.src} alt={b.name} /></li>)}</ul>
    <Link to="/about" className="hb-brands-link">Work with me <ArrowRight size={15} aria-hidden="true" /></Link>
  </section>;
}

function Loop() {
  return <section id="newsletter" className="hb-sec hb-loop" aria-labelledby="hb-loop-title">
    <div className="hb-wrap hb-loop-grid">
      <div>
        <Head id="hb-loop-title" hand="stay in the" word="loop" tone="blue" />
        <p className="hb-lede">One email a week. Mostly fabric. What I’m making, a helpful tip, and first dibs on retreats and events.</p>
        <Signup source="home-blue" className="hb-signup" button={<>Count me in <ArrowRight size={18} aria-hidden="true" /></>} done={<>You’re in! Watch your inbox <span aria-hidden="true">♡</span></>} />
        <p className="hb-loop-fine">No spam. Unsubscribe whenever.</p>
      </div>
      <div className="hb-env" aria-hidden="true">
        <div className="hb-env-back" />
        <motion.figure className="hb-env-letter" initial={{ y: 120, rotate: 0 }} whileInView={{ y: 0, rotate: -6 }} viewport={VIEW} transition={{ delay: 0.3, type: "spring", stiffness: 90, damping: 14 }}>
          <img src="/chelsea.JPG" alt="" />
        </motion.figure>
        <svg className="hb-env-front" viewBox="0 0 400 250" preserveAspectRatio="none"><path d="M0 70 L200 170 L400 70 V250 H0 Z" /><path d="M0 250 L170 150 M400 250 L230 150" /></svg>
        <p className="hb-env-addr">to: you<br />xo, chelsea <b>♥</b></p>
        <svg className="hb-env-heart" viewBox="0 0 30 46"><Ink d={HEART} delay={1} width={3.4} /></svg>
      </div>
    </div>
    <div className="hb-shelf" aria-hidden="true">
      <img className="hb-shelf-mug" src="/concept/mug.png" alt="" />
    </div>
  </section>;
}

export function Footer() {
  return <footer className="hb-foot">
    <div className="hb-wrap hb-foot-grid">
      <div>
        <Link to="/" className="hb-logo hb-foot-logo">A Few Loose Threads</Link>
        <p className="hb-note">I make colorful things (and I’d love for you to make them too).</p>
      </div>
      <nav aria-label="Footer"><ul>{footerLinks(CLUB).map(l => <li key={l.href}><Link to={l.href}>{l.label}</Link></li>)}</ul></nav>
      <div>
        <p className="hb-follow-label">Follow along</p>
        <ul className="hb-foot-social">{FOLLOW.map(f => <li key={f.s.key}><a href={f.s.url} target="_blank" rel="noreferrer" aria-label={`${f.s.platform} ${f.s.handle}`}>{f.icon}</a></li>)}</ul>
      </div>
    </div>
    <p className="hb-foot-fine">© {new Date().getFullYear()} A Few Loose Threads · made with love and too much fabric</p>
  </footer>;
}

export function HomeBlue() {
  useFonts();
  return <div className="h5 h5-pal-blue hb">
    <Hero />
    <Making />
    <Club />
    <KindWords />
    <Favorites />
    <WhereIllBe />
    <Brands />
    <Loop />
    <Footer />
  </div>;
}
